import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, Observable, of } from 'rxjs';
import { SaveTypes } from 'app/models/enums/SaveTypes';
import WorkflowHelper from '../../../../../../shared/WorkflowHelper';
import { WorkflowKeyValuePairService } from '../../../../../../services/workflow-keyvaluepair.service';
import { LOCATION_PAGE_TYPE } from '../../../../../../shared/constants';
import KeyEnum = Boo.Objects.Enums.KeyEnum;

interface ChecklistItem {
  id: string;
  label: string;
  isChecked: boolean;
  isLocationPageOnly?: boolean;
}

@Component({
  selector: 'app-components-specialist-actioncomponents-page-optimization-shared-checklist',
  templateUrl: './checklist.component.html'
})
export class PageOptimizationChecklistComponent implements OnInit {
  @Input() taskId: number;

  isLoading = true;
  showValidation = false;
  applicableItems: ChecklistItem[] = [];

  private items: ChecklistItem[] = [
    { id: 'mobileView', label: 'Mobile view checked?', isChecked: false },
    { id: 'h1MenuLink', label: 'H1 Menu link unchanged?', isChecked: false },
    { id: 'faqStyling', label: 'All FAQs on site match target styling?', isChecked: false },
    { id: 'ctasLinked', label: 'CTAs (email, phone) appropriately linked?', isChecked: false },
    { id: 'mapAdded', label: 'Map added?', isChecked: false, isLocationPageOnly: true },
    { id: 'visualCheck', label: 'Visually checked page for headings, paragraphs, & glyphs?', isChecked: false }
  ];

  constructor(
    private destroyRef: DestroyRef,
    private workflowKeyValuePairService: WorkflowKeyValuePairService) { }

  ngOnInit(): void {
    // Start without the location-only items so a failed page type lookup still shows and
    // enforces the checks that apply to every page.
    this.setApplicableItems(false);

    this.workflowKeyValuePairService.getByTaskId(this.taskId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (keyValuePairs) => this.setApplicableItems(WorkflowHelper.get<string>(keyValuePairs, KeyEnum.PageType)?.trim() === LOCATION_PAGE_TYPE),
        error: (err) => toastr.error('Failed to load page type:', err)
      });
  }

  toggle(item: ChecklistItem): void {
    item.isChecked = !item.isChecked;

    if (this.showValidation) {
      this.showValidation = !this.isEveryItemChecked();
    }
  }

  // These checks are not required for saving or rejecting the task
  isValid(saveType: SaveTypes): boolean {
    if (saveType !== SaveTypes.Complete) {
      return true;
    }

    this.showValidation = !this.isEveryItemChecked();
    return !this.showValidation;
  }

  save(): Observable<void> {
    return of(void 0);
  }

  private setApplicableItems(isLocationPage: boolean): void {
    this.applicableItems = this.items.filter(x => !x.isLocationPageOnly || isLocationPage);
  }

  private isEveryItemChecked(): boolean {
    return this.applicableItems.every(x => x.isChecked);
  }
}
